// sync-debug-view.util.ts — pure helpers behind the debug dock: the transcript
// flow with gap cards merged in at their time, the ticket-ready report text, and
// the loudness-envelope decode for the waveform rows.

import { findingLabel, type Finding } from './sync-findings.util';
import { mmss, type TimedTurn } from './transcript-timing.util';

export type FlowItem =
  | { kind: 'turn'; row: TimedTurn; index: number }
  | { kind: 'gap'; finding: Finding };

/**
 * Turns and findings as one list in playback order. A finding is placed before
 * the first windowed turn that starts at or after it, and right after a turn
 * whose window it started inside; turns without a window keep their place.
 */
export function debugFlowOf(rows: TimedTurn[], findings: Finding[]): FlowItem[] {
  const queue = [...findings].sort((a, b) => a.t0 - b.t0);
  const out: FlowItem[] = [];
  const flushUpTo = (sec: number) => {
    while (queue.length && queue[0].t0 <= sec) out.push({ kind: 'gap', finding: queue.shift()! });
  };
  rows.forEach((row, index) => {
    if (row.start != null) flushUpTo(row.start);
    out.push({ kind: 'turn', row, index });
    if (row.end != null) flushUpTo(row.end);
  });
  while (queue.length) out.push({ kind: 'gap', finding: queue.shift()! });
  return out;
}

/** Plain text a person can paste into a ticket: one line per finding + evidence. */
export function findingsReport(sessionId: string, findings: Finding[]): string {
  const lines = [`Session ${sessionId} · ${findings.length} sync finding(s)`];
  for (const f of findings) {
    lines.push(`${findingLabel(f.kind)} · ${f.who} · ${mmss(f.t0)}–${mmss(f.t1)}`);
    for (const e of f.evidence) lines.push(`  - ${e}`);
  }
  return lines.join('\n');
}

/** Base64 uint8 bins → levels (0–255); empty when absent. */
export function decodeEnvelope(b64: string | null | undefined): Uint8Array {
  if (!b64) return new Uint8Array(0);
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return new Uint8Array(0);
  }
}
